import { useState } from 'react';
import { NEW_UI } from '../app/features';
import s from './choice.module.css';

/** Segmented control and chip group: radio semantics, used across the forms. */
export function Segmented<T extends string>({ label, value, items, onChange }: { label: string; value: T; items: Array<{ id: T; label: string }>; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className={s.segmented}>
      {items.map((i) => (
        <button key={i.id} type="button" role="radio" aria-checked={i.id === value} className={i.id === value ? `${s.segment} ${s.segmentOn}` : s.segment} onClick={() => onChange(i.id)}>
          {i.label}
        </button>
      ))}
    </div>
  );
}

/**
 * limit: show only the first N chips plus a «Ещё N» chip that reveals the rest; a chosen chip beyond the limit
 * stays visible. Objects may define a dozen visit purposes, and the whole list overloads the order sheet.
 * The new interface lays the chips in one row that scrolls sideways (#4216): no second line, no «Ещё N».
 */
export function Chips<T extends string | number>({ label, value, items, onChange, allowNone, limit }: { label: string; value: T | null; items: Array<{ id: T; label: string; disabled?: boolean }>; onChange: (v: T | null) => void; allowNone?: boolean; limit?: number }) {
  const [expanded, setExpanded] = useState(false);
  const collapsed = !NEW_UI && !expanded && limit !== undefined && items.length > limit + 1;
  const shown = collapsed ? items.filter((i, n) => n < limit || i.id === value) : items;
  return (
    <div role="radiogroup" aria-label={label} className={NEW_UI ? `${s.chips} ${s.slider}` : s.chips}>
      {shown.map((i) => (
        <button
          key={String(i.id)}
          type="button"
          role="radio"
          aria-checked={i.id === value}
          disabled={i.disabled}
          className={i.id === value ? `${s.chip} ${s.chipOn}` : s.chip}
          onClick={() => onChange(allowNone && i.id === value ? null : i.id)}
        >
          {i.label}
        </button>
      ))}
      {collapsed && (
        <button type="button" className={`${s.chip} ${s.chipMore}`} onClick={() => setExpanded(true)}>
          Ещё {items.length - shown.length}
        </button>
      )}
    </div>
  );
}
